<script>
export default {
    name: "MapUserPanel",
    emits: ["close", "focus"],
    props: {
        tenantList: {
            type: Array,
            default: () => [],
            required: true,
        },
        users: {
            type: Array,
            default: () => [],
            required: true,
        },
    },
    data() {
        return {
            textFilter: "",
            cnt: 0,
        };
    },
    computed: {
        userList() {
            const regexp = new RegExp(RegExp.escape(this.textFilter), "i");
            return this.users
                .filter(u => regexp.test(u.displayName))
                .map(u => ({
                    tenantId: u.userTenantId,
                    userId: u.userId,
                    name: u.displayName,
                    tenantName: this.tenantNames.get(u.userTenantId) ?? "",
                    isActive: u.isActive ?? false,
                }));
        },
        tenantNames() {
            const getTenantName = (tenantId) => {
                const tenantName = this.tenantList.find(t => t.tenantId === tenantId)?.name ?? "";
                return tenantName === "ROOT-NODE" ? "" : tenantName;
            };
            return new Map(this.tenantList.map(t => [t.tenantId, getTenantName(t.tenantId)]));
        },
        isSearching() {
            return this.textFilter.length > 0;
        },
    },
    methods: {
        handleOnSelectUser(e) {
            e.preventDefault();
            e.stopPropagation();

            const user = e.target?.closest?.(".user");
            if (!user) return;

            const tenantId = user.dataset.tenantId;
            const userId = user.dataset.userId;
            this.$emit("focus", {tenantId, userId});
        },
        getTitleAttributeForUser(user) {
            return user.tenantName ? `${user.name}@${user.tenantName}` : user.name;
        },
    },
}
</script>

<template>
    <div class="container">
        <div class="title-wrapper">
            <h2 class="title">{{ $t("message.title_map_user_list") }}</h2>
            <button @click="() => $emit('close')" class="close-button" type="button">
                <img :src="require('@/assets/icon_clear.svg')" alt="close" class="close-icon"/>
            </button>
        </div>
        <div class="filter-wrapper">
            <div class="text-filter__label">{{ $t("message.item_user_name") }}</div>
            <el-input v-model.trim="textFilter" clearable></el-input>
        </div>
        <div class="users-wrapper" @click="handleOnSelectUser">
            <div v-if="userList.length > 0">
                <div v-for="u in userList"
                     class="user"
                     :title="getTitleAttributeForUser(u)"
                     :data-tenant-id="u.tenantId"
                     :data-user-id="u.userId">
                    <img :src="require('@/assets/user.svg')" alt="user" class="user-icon"
                         :class="{'user-icon--inactive' : !u.isActive}"/>
                    <div class="user__info">
                        <div class="user__info-user-name">{{ u.name }}</div>
                        <div class="user__info-tenant-name">{{ u.tenantName }}</div>
                    </div>
                </div>
            </div>
            <div v-else class="error-no-users">
                <div v-if="isSearching">{{ $t("message.error_no_matching_users") }}</div>
                <div v-else>{{ $t("message.error_no_users") }}</div>
            </div>
        </div>
    </div>
</template>

<style scoped>
/* overwrite browser default styles */
* {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
}

.container {
    height: 100%;
    display: flex;
    flex-direction: column;
}

.title-wrapper {
    flex-grow: 0;
    padding: 4px 8px;
    display: flex;
    align-items: center;

    .title {
        font-size: 1rem;
        font-weight: 700;
    }

    .close-button {
        margin-left: auto;
        padding: 8px;
        height: 40px;
        width: 40px;
        border: none;
        background-color: transparent;

        &:hover {
            cursor: pointer;
        }
    }
}

.divider {
    flex-grow: 0;
    margin: 4px 0;
    border: none;
    border-top: 1px solid #ccc;
}

.filter-wrapper {
    flex-grow: 0;
    padding: 8px;

    .text-filter__label {
        font-size: 10px;
        color: rgba(0, 0, 0, 0.5);
    }
}

.users-wrapper {
    flex-grow: 1;
    overflow: auto;

    .user {
        display: flex;
        align-items: center;
        justify-content: start;
        column-gap: 8px;
        height: 44px;
        padding: 4px 8px;

        &:hover {
            cursor: pointer;
            background-color: rgba(0, 0, 0, 0.05);
        }

        .user-icon {
            height: 16px;
            width: 16px;
            flex-grow: 0;

            &.user-icon--inactive {
                filter: grayscale(1);
            }
        }

        .user__info {
            flex-grow: 1;
            min-width: 0;

            .user__info-user-name {
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }

            .user__info-tenant-name {
                font-size: 10px;
                color: rgba(0, 0, 0, 0.5);
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }
        }
    }
}

.error-no-users {
    padding: 4px 8px;
    color: rgba(0, 0, 0, 0.5);
}
</style>
